import type { ReactNode } from "react";

/**
 * Minimal admin-authored formatting for learner-facing text (programme
 * descriptions etc): blank line = new paragraph, "- " starts a bullet list,
 * **bold** and *italic*. Deliberately not full Markdown/HTML — this is short
 * admin-written summary text, not arbitrary documents, and never touches
 * dangerouslySetInnerHTML.
 */
export function RichText({ text }: { text: string }) {
  const blocks = text.trim().split(/\n{2,}/);
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        const isList = lines.length > 0 && lines.every((l) => l.startsWith("- "));
        if (isList) {
          return (
            <ul
              key={bi}
              style={{ margin: bi === 0 ? "0 0 0.5rem" : "0.5rem 0", paddingLeft: "1.25rem" }}
            >
              {lines.map((l, li) => (
                <li key={li}>{renderInline(l.slice(2), `${bi}-${li}`)}</li>
              ))}
            </ul>
          );
        }
        const blockLines = block.split("\n");
        return (
          <p key={bi} style={{ margin: bi === 0 ? 0 : "0.5rem 0 0" }}>
            {blockLines.map((line, li) => (
              <span key={li}>
                {renderInline(line, `${bi}-${li}`)}
                {li < blockLines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </>
  );
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = re.exec(text))) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    if (match[1] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-${i++}`}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      nodes.push(<em key={`${keyPrefix}-${i++}`}>{match[2]}</em>);
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}
